import { CrossCircleIcon, FilterIcon, StartActiveIcon } from "assets/icons";
import { toastServerError } from "helpers";
import moment from "moment";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  FilterChips,
  Input,
  Modal,
  SearchInput
} from "ui";

import {
  useAffinityBlacklist,
  useAffinityRules,
  useAffinityStatus,
  useBlockAffinityPairing,
  useDeleteAffinityRule,
  useGenerateAffinity,
  useLearnFromOrders,
  useUnblockAffinityPairing
} from "../../apis";
import {
  AffinityAddForm,
  AffinityBlacklistForm,
  AffinityBlacklistList,
  AffinityGroups,
  SectionCard
} from "../../components";
import { AFFINITY_SOURCE_LABELS } from "../../constants";
import {
  AffinityBlacklistEntry,
  AffinityRule,
  EAffinitySource
} from "../../recommendation.types";

import styles from "./AffinityGraphEditor.module.scss";

const SOURCE_FILTER_OPTIONS = [
  { label: "All", value: "all" },
  ...Object.values(EAffinitySource).map((source) => ({
    label: AFFINITY_SOURCE_LABELS[source],
    value: source
  }))
];

const matchesSearch = (search: string, ...names: Array<string | null>) =>
  !search ||
  names.some((name) => name?.toLowerCase().includes(search.toLowerCase()));

const AffinityGraphEditor = () => {
  const { t } = useTranslation();

  const { rules, isLoading } = useAffinityRules();
  const { affinityStatus } = useAffinityStatus();
  const { blacklist, isLoading: isBlacklistLoading } = useAffinityBlacklist();

  const [ruleToDelete, setRuleToDelete] = useState<AffinityRule | null>(null);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [ruleToBlock, setRuleToBlock] = useState<AffinityRule | null>(null);
  const [blockBothDirections, setBlockBothDirections] = useState(true);
  const [blockReason, setBlockReason] = useState("");
  const [unblockingId, setUnblockingId] = useState<string | null>(null);

  const filteredRules = useMemo(
    () =>
      rules.filter(
        (rule) =>
          (sourceFilter === "all" || rule.source === sourceFilter) &&
          matchesSearch(search.trim(), rule.fromName, rule.toName)
      ),
    [rules, search, sourceFilter]
  );

  const filteredBlacklist = useMemo(
    () =>
      blacklist.filter((entry) =>
        matchesSearch(search.trim(), entry.fromName, entry.toName)
      ),
    [blacklist, search]
  );

  const { mutate: blockPairing, isLoading: isBlocking } =
    useBlockAffinityPairing({
      onSuccess: () => {
        toast.success(t("Pairing blocked"));
        setRuleToBlock(null);
      },
      onError: toastServerError
    });

  const { mutate: unblockPairing } = useUnblockAffinityPairing({
    onSuccess: () => {
      toast.success(t("Pairing unblocked"));
      setUnblockingId(null);
    },
    onError: (error) => {
      setUnblockingId(null);
      toastServerError(error);
    }
  });

  const openBlockModal = (rule: AffinityRule) => {
    setBlockBothDirections(true);
    setBlockReason("");
    setRuleToBlock(rule);
  };

  const handleUnblock = (entry: AffinityBlacklistEntry) => {
    setUnblockingId(entry.id);
    unblockPairing(entry.id);
  };

  const { mutate: deleteRule, isLoading: isDeleting } = useDeleteAffinityRule({
    onSuccess: () => {
      toast.success(t("Affinity rule deleted"));
      setRuleToDelete(null);
    },
    onError: toastServerError
  });

  const { mutate: learn, isLoading: isLearning } = useLearnFromOrders({
    onSuccess: (result) =>
      toast.success(
        `${t("Learned")}: ${result.cooccurrencePairs} ${t(
          "product pairs"
        )}, ${result.affinityRules} ${t("category rules")}`
      ),
    onError: toastServerError
  });

  const { mutate: generate, isLoading: isGenerating } = useGenerateAffinity({
    onSuccess: (result) => {
      setIsGenerateModalOpen(false);
      toast.success(
        `${t("AI generated")}: ${result.stored} ${t("rules stored")} (${
          result.proposed
        } ${t("proposed")})`
      );
    },
    onError: toastServerError
  });

  // The status endpoint counts by source authoritatively; the local tally is the
  // fallback for the moment before it lands.
  const manualCount =
    affinityStatus?.rules.manual ??
    rules.filter((rule) => rule.source === "manual").length;
  const aiCount =
    affinityStatus?.rules.ai ?? rules.filter((rule) => rule.source === "ai").length;
  const learnedCount =
    affinityStatus?.rules.learned ??
    rules.filter((rule) => rule.source === "learned").length;

  return (
    <div className={styles.editor}>
      <div className={styles.editor__intro}>
        <p className={styles.editor__hint}>
          {t(
            "The \"goes with\" graph is directional category → category pairs. The recommendation cascade uses it to jump from a product's category to complementary ones. Curate manual rules and let AI propose the rest."
          )}
        </p>

        <div className={styles.editor__actions}>
          <Button
            variant="default"
            loading={isLearning}
            onClick={() => learn()}
          >
            {t("Learn from orders")}
          </Button>

          <Button
            variant="default"
            icon={
              <span className={styles.editor__genIcon}>
                <StartActiveIcon width={16} height={16} />
              </span>
            }
            onClick={() => setIsGenerateModalOpen(true)}
          >
            {t("Generate with AI")}
          </Button>
        </div>
      </div>

      <div className={styles.editor__stats}>
        <div className={styles.editor__stat}>
          <span className={styles.editor__statValue}>{rules.length}</span>
          <span className={styles.editor__statLabel}>{t("rules total")}</span>
        </div>
        <div className={styles.editor__stat}>
          <span className={styles.editor__statValue}>{manualCount}</span>
          <span className={styles.editor__statLabel}>{t("manual")}</span>
        </div>
        <div className={styles.editor__stat}>
          <span className={styles.editor__statValue}>{aiCount}</span>
          <span className={styles.editor__statLabel}>{t("AI-generated")}</span>
        </div>
        <div className={styles.editor__stat}>
          <span className={styles.editor__statValue}>{learnedCount}</span>
          <span className={styles.editor__statLabel}>
            {t("learned from orders")}
          </span>
        </div>
        <div className={styles.editor__stat}>
          <span className={styles.editor__statValue}>{blacklist.length}</span>
          <span className={styles.editor__statLabel}>
            {t("blocked pairings")}
          </span>
        </div>
      </div>

      {affinityStatus && (
        <p className={styles.editor__provenance}>
          {affinityStatus.lastGeneratedAt
            ? `${t("Last AI generation")} ${moment(affinityStatus.lastGeneratedAt).format(
                "DD/MM/YYYY HH:mm"
              )}.`
            : `${t("AI generation has never run.")}`}{" "}
          {affinityStatus.catalogueChanged
            ? t("The catalogue has changed since — regenerating would see new categories.")
            : t("The catalogue is unchanged — regenerating would rewrite identical rules.")}
        </p>
      )}

      <SectionCard icon={<FilterIcon />} title={t("Add a rule")}>
        <AffinityAddForm />
      </SectionCard>

      <div className={styles.editor__filters}>
        <SearchInput
          value={search}
          placeholder="Search by category"
          onChange={setSearch}
        />
        <FilterChips
          options={SOURCE_FILTER_OPTIONS}
          value={sourceFilter}
          onChange={setSourceFilter}
        />
      </div>

      <AffinityGroups
        rules={filteredRules}
        isLoading={isLoading}
        emptyText={
          rules.length ? t("No rules match these filters.") : undefined
        }
        onDelete={setRuleToDelete}
        onBlock={openBlockModal}
      />

      <SectionCard
        icon={<CrossCircleIcon />}
        accentColor="#8E8E93"
        title={t("Blacklist")}
        subtitle={t(
          "Pairings the engine never makes, whoever proposes them: manual rules, AI generation and learning from orders all skip them. Block a parent category to cover every category under it."
        )}
      >
        <div className={styles.editor__blacklist}>
          <AffinityBlacklistForm />
          <AffinityBlacklistList
            entries={filteredBlacklist}
            isLoading={isBlacklistLoading}
            unblockingId={unblockingId}
            emptyText={
              blacklist.length
                ? t("No blocked pairings match this search.")
                : t("Nothing is blocked.")
            }
            onUnblock={handleUnblock}
          />
        </div>
      </SectionCard>

      <Modal
        isOpen={isGenerateModalOpen}
        isLoading={isGenerating}
        title={t("Generate affinity graph with AI")}
        okText={t("Generate")}
        onClose={() => setIsGenerateModalOpen(false)}
        onOk={() => generate()}
      >
        <p className={styles.confirmText}>
          {t(
            "The AI will propose the whole graph from the category tree. This replaces all existing AI rules but keeps your manual and learned ones. Takes a few seconds."
          )}
        </p>
      </Modal>

      <Modal
        isOpen={Boolean(ruleToBlock)}
        isLoading={isBlocking}
        title={t("Block pairing")}
        okText={t("Block")}
        onClose={() => setRuleToBlock(null)}
        onOk={() =>
          ruleToBlock &&
          blockPairing({
            fromCategoryId: ruleToBlock.fromCategoryId,
            toCategoryId: ruleToBlock.toCategoryId,
            bothDirections: blockBothDirections,
            reason: blockReason.trim() || undefined
          })
        }
      >
        <div className={styles.blockForm}>
          <p className={styles.confirmText}>
            {ruleToBlock
              ? `${t("Remove")} "${ruleToBlock.fromName ?? "?"} → ${
                  ruleToBlock.toName ?? "?"
                }" ${t(
                  "and never pair these categories again — not by AI generation, and not by learning from orders."
                )}`
              : ""}
          </p>
          <Checkbox
            label={t("Both directions")}
            checked={blockBothDirections}
            onChange={(event) => setBlockBothDirections(event.target.checked)}
          />
          <Input
            label={t("Reason (optional)")}
            placeholder={t("Why this pairing makes no sense")}
            value={blockReason}
            maxLength={200}
            onChange={(event) => setBlockReason(event.target.value)}
          />
        </div>
      </Modal>

      <Modal
        type="delete"
        isOpen={Boolean(ruleToDelete)}
        isLoading={isDeleting}
        title={t("Delete affinity rule")}
        okText={t("Delete")}
        onClose={() => setRuleToDelete(null)}
        onOk={() => ruleToDelete && deleteRule(ruleToDelete.id)}
      >
        <p className={styles.confirmText}>
          {ruleToDelete
            ? `${t("Delete the rule")} "${
                ruleToDelete.fromName ?? "?"
              } → ${ruleToDelete.toName ?? "?"}"?`
            : ""}
        </p>
      </Modal>
    </div>
  );
};

export default AffinityGraphEditor;
